import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useFormatMoneda } from '../utils/moneda';

export default function Ventas() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const formatMoneda = useFormatMoneda();
  const [ventas, setVentas] = useState([]);
  const [detalle, setDetalle] = useState(null);
  const [filtros, setFiltros] = useState({ cliente_id: '', estado: '', desde: '', hasta: '' });
  const [loading, setLoading] = useState(false);

  const cargar = useCallback(() => {
    const params = {};
    if (filtros.estado) params.estado = filtros.estado;
    if (filtros.desde) params.desde = filtros.desde;
    if (filtros.hasta) params.hasta = filtros.hasta;
    api.get('/ventas', { params }).then(r => setVentas(r.data)).catch(() => {});
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  async function verDetalle(id) {
    try {
      const res = await api.get(`/ventas/${id}`);
      setDetalle(res.data);
    } catch { toast.error('Error al cargar detalle'); }
  }

  async function anular(id) {
    if (!window.confirm('¿Anular esta venta? Se restaurará el stock y se cancelará el crédito.')) return;
    setLoading(true);
    try {
      await api.put(`/ventas/${id}/anular`);
      toast.success('Venta anulada y stock restaurado');
      setDetalle(null);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo anular la venta');
    } finally { setLoading(false); }
  }

  const esAdmin = ['admin', 'supervisor', 'cobrador'].includes(usuario?.rol);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>Ventas a Crédito</h1>
        {esAdmin && (
          <button onClick={() => navigate('/ventas/nueva')} style={styles.btn}>+ Nueva Venta</button>
        )}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <select value={filtros.estado} onChange={e => setFiltros(f => ({ ...f, estado: e.target.value }))} style={styles.input}>
          <option value="">Todas</option>
          <option value="activa">Activas</option>
          <option value="anulada">Anuladas</option>
        </select>
        <input type="date" value={filtros.desde} onChange={e => setFiltros(f => ({ ...f, desde: e.target.value }))} style={styles.input} placeholder="Desde" />
        <input type="date" value={filtros.hasta} onChange={e => setFiltros(f => ({ ...f, hasta: e.target.value }))} style={styles.input} placeholder="Hasta" />
        <button onClick={() => setFiltros({ cliente_id: '', estado: '', desde: '', hasta: '' })} style={styles.btnSecundario}>Limpiar</button>
      </div>

      {/* Tabla */}
      <div style={{ background: 'var(--bg-card)', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-app)' }}>
              {['#', 'Cliente', 'Total', 'Ítems', 'Estado', 'Vendedor', 'Fecha', ''].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ventas.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--text-4)' }}>No hay ventas registradas</td></tr>
            )}
            {ventas.map(v => (
              <tr key={v.id} style={{ borderBottom: '1px solid var(--border-2)', cursor: 'pointer' }} onClick={() => verDetalle(v.id)}>
                <td style={styles.td}><span style={{ color: 'var(--text-3)' }}>#{v.id}</span></td>
                <td style={styles.td}>
                  <div style={{ fontWeight: 600 }}>{v.cliente_nombre}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{v.cliente_documento}</div>
                </td>
                <td style={styles.td}><strong style={{ color: '#34d399' }}>{formatMoneda(v.total)}</strong></td>
                <td style={styles.td}><span style={{ color: 'var(--text-2)' }}>{v.total_items} ítem(s)</span></td>
                <td style={styles.td}>
                  <span style={{ background: v.estado === 'activa' ? '#0f3d2e' : '#3d0f0f', color: v.estado === 'activa' ? '#34d399' : '#f87171', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                    {v.estado}
                  </span>
                </td>
                <td style={styles.td}><span style={{ color: 'var(--text-2)', fontSize: 13 }}>{v.vendedor_nombre}</span></td>
                <td style={styles.td}><span style={{ color: 'var(--text-3)', fontSize: 12 }}>{v.created_at?.split('T')[0]}</span></td>
                <td style={styles.td}>
                  <span style={{ color: '#38bdf8', fontSize: 13 }}>Ver →</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal detalle */}
      {detalle && (
        <div style={styles.overlay} onClick={e => { if (e.target === e.currentTarget) setDetalle(null); }}>
          <div style={styles.modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0 }}>Venta #{detalle.id}</h2>
              <button onClick={() => setDetalle(null)} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 22 }}>×</button>
            </div>

            {/* Info cliente */}
            <div style={{ background: 'var(--bg-app)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div><span style={styles.label2}>Cliente</span><strong>{detalle.cliente_nombre}</strong></div>
                <div><span style={styles.label2}>Documento</span><strong>{detalle.cliente_documento}</strong></div>
                <div><span style={styles.label2}>Teléfono</span><strong>{detalle.cliente_telefono || '—'}</strong></div>
                <div><span style={styles.label2}>Vendedor</span><strong>{detalle.vendedor_nombre}</strong></div>
                <div><span style={styles.label2}>Fecha</span><strong>{detalle.created_at?.split('T')[0]}</strong></div>
                <div>
                  <span style={styles.label2}>Estado</span>
                  <span style={{ background: detalle.estado === 'activa' ? '#0f3d2e' : '#3d0f0f', color: detalle.estado === 'activa' ? '#34d399' : '#f87171', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                    {detalle.estado}
                  </span>
                </div>
              </div>
            </div>

            {/* Productos */}
            <h3 style={{ color: 'var(--text-2)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Productos</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
              <thead>
                <tr style={{ background: 'var(--bg-app)' }}>
                  {['Producto', 'Ref', 'Cant.', 'P. Unit.', 'Subtotal'].map(h => <th key={h} style={styles.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {detalle.items?.map(item => (
                  <tr key={item.id} style={{ borderBottom: '1px solid var(--border-2)' }}>
                    <td style={styles.td}>{item.producto_nombre}</td>
                    <td style={styles.td}><span style={{ color: 'var(--text-3)' }}>{item.referencia || '—'}</span></td>
                    <td style={styles.td}>{item.cantidad}</td>
                    <td style={styles.td}>{formatMoneda(item.precio_unitario)}</td>
                    <td style={styles.td}><strong style={{ color: '#34d399' }}>{formatMoneda(item.subtotal)}</strong></td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={4} style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>Total</td>
                  <td style={{ ...styles.td, color: '#38bdf8', fontWeight: 700, fontSize: 18 }}>{formatMoneda(detalle.total)}</td>
                </tr>
              </tbody>
            </table>

            {/* Crédito vinculado */}
            {detalle.credito && (
              <>
                <h3 style={{ color: 'var(--text-2)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Crédito generado</h3>
                <div style={{ background: 'var(--bg-app)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    <div><span style={styles.label2}>Crédito #</span><strong>{detalle.credito.id}</strong></div>
                    <div><span style={styles.label2}>Estado</span><strong style={{ color: detalle.credito.estado === 'activo' ? '#34d399' : '#94a3b8' }}>{detalle.credito.estado}</strong></div>
                    <div><span style={styles.label2}>Frecuencia</span><strong>{detalle.credito.frecuencia}</strong></div>
                    <div><span style={styles.label2}>Cuotas</span><strong>{detalle.credito.cuotas_pagadas}/{detalle.credito.total_cuotas} pagadas</strong></div>
                    <div><span style={styles.label2}>Total pagado</span><strong style={{ color: '#34d399' }}>{formatMoneda(detalle.credito.total_pagado || 0)}</strong></div>
                    <div><span style={styles.label2}>Saldo</span><strong style={{ color: '#fbbf24' }}>{formatMoneda(detalle.total - (detalle.credito.total_pagado || 0))}</strong></div>
                  </div>
                </div>
              </>
            )}

            {/* Acciones */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {esAdmin && detalle.estado === 'activa' && (
                <button onClick={() => anular(detalle.id)} disabled={loading} style={{ background: '#3d0f0f', color: '#f87171', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                  {loading ? '...' : 'Anular venta'}
                </button>
              )}
              <button onClick={() => setDetalle(null)} style={styles.btnSecundario}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  btn: { background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  btnSecundario: { background: 'var(--bg-card)', color: 'var(--text-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 14 },
  input: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text-1)', fontSize: 14 },
  th: { padding: '10px 14px', textAlign: 'left', fontSize: 12, color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
  td: { padding: '12px 14px', fontSize: 14, color: 'var(--text-1s)', verticalAlign: 'middle' },
  label2: { display: 'block', color: 'var(--text-3)', fontSize: 11, marginBottom: 2 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'var(--bg-card)', borderRadius: 16, padding: 28, width: '90%', maxWidth: 760, maxHeight: '90vh', overflowY: 'auto' },
};
